import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, MapPin, Calendar as CalendarIcon, Camera, X } from "lucide-react";
import { compressImage } from "@/utils/image";

const VisitDialog = ({ open, restaurant, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [visitDate, setVisitDate] = useState(new Date());
  const [visitCount, setVisitCount] = useState(1);
  const [visitNotes, setVisitNotes] = useState("");
  const [visitPhotos, setVisitPhotos] = useState([]);
  const [visitCost, setVisitCost] = useState("");
  const [visitCurrency, setVisitCurrency] = useState("EUR");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const resetForm = () => {
    setVisitDate(new Date());
    setVisitCount(1);
    setVisitNotes("");
    setVisitPhotos([]);
    setVisitCost("");
    setVisitCurrency("EUR");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const dataUrl = await compressImage(file);
      const response = await axios.post(`${API}/upload/photo`,
        { photo: dataUrl },
        { withCredentials: true }
      );
      setVisitPhotos(prev => [...prev, response.data.photo_id]);
      toast.success("Foto hochgeladen!");
    } catch (error) {
      toast.error("Foto-Upload fehlgeschlagen");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (photoId) => {
    setVisitPhotos(prev => prev.filter(id => id !== photoId));
    axios.delete(`${API}/upload/photo/${photoId}`, { withCredentials: true }).catch(() => {});
  };

  const handleSubmit = async () => {
    if (!restaurant) return;
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/visits`, {
        restaurant_id: restaurant.restaurant_id,
        restaurant_name: restaurant.name,
        stars: restaurant.stars,
        visit_date: format(visitDate, "yyyy-MM-dd"),
        visit_count: visitCount,
        notes: visitNotes || null,
        photos: visitPhotos,
        cost: visitCost ? parseFloat(visitCost) : null,
        currency: visitCurrency
      }, { withCredentials: true });

      if (response.data.badge_changed) {
        toast.success(`🎉 Badge unlocked: ${response.data.new_badge}!`, { duration: 5000 });
      } else {
        toast.success(`Visit recorded! You earned ${restaurant.stars * visitCount} stars!`);
      }
      resetForm();
      onSuccess();
    } catch (error) {
      toast.error("Failed to add visit");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-primary" />
            {t('restaurants.recordVisit')}
          </DialogTitle>
        </DialogHeader>

        {restaurant && (
          <div className="space-y-4">
            <div className="p-4 bg-muted/20 rounded-lg">
              <h3 className="font-semibold text-lg">{restaurant.name}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {restaurant.location}, {restaurant.country}
              </p>
              <div className="flex gap-0.5 mt-2">
                {[...Array(restaurant.stars)].map((_, i) => (
                  <Star key={`selected-star-${i}`} className="w-5 h-5 text-primary fill-primary" />
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.visitDate')}</Label>
              <Input
                type="date"
                value={format(visitDate, "yyyy-MM-dd")}
                onChange={(e) => {
                  const newDate = new Date(e.target.value);
                  if (!isNaN(newDate.getTime())) setVisitDate(newDate);
                }}
                className="w-full bg-transparent border-border focus:border-primary text-foreground touch-manipulation"
                style={{ colorScheme: 'light' }}
                max={format(new Date(), "yyyy-MM-dd")}
                data-testid="visit-date-input"
              />
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.numberOfVisits')}</Label>
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={() => setVisitCount(Math.max(1, visitCount - 1))} className="border-border" data-testid="visit-count-decrease">
                  -
                </Button>
                <span className="text-xl font-semibold w-8 text-center">{visitCount}</span>
                <Button variant="outline" size="sm" onClick={() => setVisitCount(visitCount + 1)} className="border-border" data-testid="visit-count-increase">
                  +
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.notes')}</Label>
              <Textarea
                placeholder={t('restaurants.notesPlaceholder')}
                value={visitNotes}
                onChange={(e) => setVisitNotes(e.target.value)}
                className="bg-transparent border-border focus:border-primary"
                data-testid="visit-notes"
              />
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Kosten (optional)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="0.00"
                  value={visitCost}
                  onChange={(e) => setVisitCost(e.target.value)}
                  className="flex-1 bg-transparent border-border focus:border-primary"
                  data-testid="visit-cost"
                />
                <select
                  value={visitCurrency}
                  onChange={(e) => setVisitCurrency(e.target.value)}
                  className="px-3 py-2 bg-card border border-border rounded-md text-sm"
                  data-testid="visit-currency"
                >
                  <option value="EUR">€ EUR</option>
                  <option value="USD">$ USD</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.photos')}</Label>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 px-4 py-2 border border-border rounded cursor-pointer hover:border-primary transition-colors">
                  <Camera className="w-4 h-4" />
                  <span className="text-sm">{uploadingPhoto ? "Wird hochgeladen..." : t('restaurants.addPhoto')}</span>
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} data-testid="visit-photo-input" />
                </label>
              </div>
              {visitPhotos.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap" data-testid="visit-photo-thumbnails">
                  {visitPhotos.map((photoId) => (
                    <div key={photoId} className="relative group">
                      <img
                        src={`${API}/upload/photo/${photoId}`}
                        alt=""
                        className="w-16 h-16 object-cover rounded border border-border"
                      />
                      <button
                        onClick={() => removePhoto(photoId)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-80 hover:opacity-100"
                        data-testid={`remove-photo-${photoId}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex justify-between items-center mb-4">
                <span className="text-muted-foreground">{t('restaurants.starsToEarn')}:</span>
                <span className="text-2xl font-bold gradient-text">
                  {restaurant.stars * visitCount}
                </span>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleClose} className="flex-1 border-border" data-testid="visit-cancel">
                  Abbrechen
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="visit-submit">
                  {isSubmitting ? t('restaurants.recording') : t('restaurants.recordVisit')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VisitDialog;
